import { UnisphereRuntimeLoader, UnisphereRuntimeVisual } from "@unisphere/runtime-react";
import { useCallback, useContext, useMemo } from "react";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { setGenieThreadId } from "@kaltura/mediaspace-shared-utils";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { version } from "@kaltura/mediaspace-shared-data-kms-version";
import { ChatRuntimeName, WidgetName } from "@unisphere/genie-core";
import { widgetName as PlgWidgetName, WidgetsRuntimeName } from "@kaltura-corp/unisphere-plg-types";
import { styled } from "@mui/material";
import { Box, systemWidth } from "@kaltura/mediaspace-shared-styled";
import { headerHeight } from "@kaltura/mediaspace-shared-constants";
import { sidebarButtonsSize, useIsMobileSidebarLayout, useSidebarLayoutContext } from "@kaltura/ds-react-layouts";
import { buildBaseGenieSettings, getCategoryUrl, getGenieContext } from "./helper";
import { usePlgWidgetSettings } from "./usePlgWidgetSettings";
import { BaseGenieProps } from "./types";
import { ChatRuntimeSettings, ContainedVisualSettings } from "@unisphere/genie-types";

export interface GenieProps extends BaseGenieProps {
    genieServerUrl: string;
    uiconfId: string;
    isFullSize?: boolean;
    onExpand?: () => void;
    onClose?: () => void;
    isExpanded?: boolean;
    forceSingleThread?: boolean;
    forcedThreadId?: string;
    // Target of links rendered in Genie's markdown (inside the chat window)
    // If not provided, Genie will use the default target ("_blank").
    mdLinksTarget?: "_blank" | " _self" | "_parent" | "_top";
    /** Show/hide the AI model selector (Fast/Smart). Default: true */
    isModalSelectorVisible?: boolean;
    /** Show/hide the context selector chip and add-context button. Default: true */
    isContextSelectorVisible?: boolean;
    /** type of close button to render. Default: "drawer" */
    closeButtonType?: "none" | "drawer";
    /** constrain the widget to the system width layout, for styling purposes when embedded in template pages */
    constrainToSystemWidth?: boolean;
    /** source page url to be used for Genie context - relevant for Genie component in template pages */
    sourcePageUrl?: string;
    height?: string;
}

/**
 * Genie widget
 * renders a Genie chat widget
 * used in ds pages
 *
 * @page-component genie-page
 */
export const Genie = ({
    genieServerUrl,
    ks,
    uiconfId,
    categoryData,
    isFullSize = false,
    agentMode = false,
    entryData,
    visualType = "page",
    onExpand,
    onClose,
    isExpanded = false,
    mode = "chat",
    forceSingleThread = false,
    forcedThreadId,
    mdLinksTarget,
    isModalSelectorVisible = true,
    isContextSelectorVisible = true,
    closeButtonType = "drawer",
    constrainToSystemWidth = false,
    sourcePageUrl,
    height,
}: GenieProps) => {
    const {
        application: { partnerId, serviceUrl },
        analytics,
    } = useConfig();

    const configContext = useContext(ConfigContext);
    const headerColor = configContext.dsLayout?.headerColor;
    const topSpacing = headerColor && headerColor !== "transparent" ? headerHeight : 0;

    const { isEnabled } = useSidebarLayoutContext();
    const isMobileSidebarLayout = useIsMobileSidebarLayout();
    const bottomSpacing = isEnabled && isMobileSidebarLayout ? sidebarButtonsSize : 0;

    // Calculate minHeight style for full size mode container
    // irrelevant if height is provided (height takes precedence over minHeight)
    const minHeight = isFullSize && !height ? `calc(100vh - ${topSpacing + bottomSpacing}px)` : undefined;

    const categoryUrl = getCategoryUrl(categoryData);

    const settings = useMemo<ChatRuntimeSettings>(
        () => ({
            ...buildBaseGenieSettings({
                ks,
                partnerId,
                agentMode,
                categoryData,
                categoryUrl,
                entryData,
                mode,
                sourcePageUrl,
            }),
            genieServerUrl,
            kalturaServerURI: serviceUrl,
            uiconfId,
            analytics: {
                enabled: true,
                serverUrl: "https://" + analytics.serviceUrl,
                appName: analytics.appName,
                appVersion: version,
            },
            initialThread: forcedThreadId ? { type: "threadId", threadId: forcedThreadId } : undefined,
        }),
        [
            analytics.appName,
            analytics.serviceUrl,
            categoryData,
            categoryUrl,
            genieServerUrl,
            ks,
            partnerId,
            serviceUrl,
            uiconfId,
            agentMode,
            entryData,
            mode,
            forcedThreadId,
            sourcePageUrl,
        ]
    );

    const stableOnClose = useEventHandler(onClose);
    const stableOnExpand = useEventHandler(onExpand);

    // kept for the tab, so feedback sent from any page can reference the conversation
    const handleThreadIdChanged = useCallback((threadId: string) => setGenieThreadId(threadId), []);

    const visualSettings = useMemo((): ContainedVisualSettings => {
        const context = getGenieContext({ entryData, categoryData, sourcePageUrl });
        if (mode === "chat") {
            // chat mode - no close/expand, context without title prop
            delete context?.title;
            return { context, onThreadIdChanged: handleThreadIdChanged };
        }
        return {
            scroll: isExpanded ? undefined : "container",
            onRequestClose: stableOnClose,
            onExpand: stableOnExpand,
            onThreadIdChanged: handleThreadIdChanged,
            isExpanded,
            closeButtonType,
            mode,
            ...(context ? { context } : {}),
            thread: {
                enabled: !forceSingleThread,
            },
            general: {
                linkTarget: mdLinksTarget,
            },
            uiControls: {
                modalSelector: { visible: isModalSelectorVisible },
                contextSelector: { visible: isContextSelectorVisible },
            },
        };
        // !! because stableOnClose/stableOnExpand are always defined (useEventHandler always returns a wrapper),
        // so we track presence of the original props as booleans to avoid re-running on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps,react-hooks/use-memo
    }, [
        !!onClose,
        !!onExpand,
        stableOnClose,
        stableOnExpand,
        handleThreadIdChanged,
        entryData,
        categoryData,
        isExpanded,
        mode,
        sourcePageUrl,
        closeButtonType,
    ]);

    const plgWidgetSettings = usePlgWidgetSettings(ks);

    return (
        <StyledOuterWrapper
            isFullSize={isFullSize}
            minHeight={minHeight}
            constrainToSystemWidth={constrainToSystemWidth}
            height={height}
        >
            <UnisphereRuntimeLoader
                widgetName={WidgetName}
                runtimeName={ChatRuntimeName}
                runtimeSettings={settings as unknown as Record<string, unknown>}
            />
            <UnisphereRuntimeLoader
                widgetName={PlgWidgetName}
                runtimeName={WidgetsRuntimeName}
                runtimeSettings={plgWidgetSettings as unknown as Record<string, unknown>}
            />
            <UnisphereRuntimeVisual
                visualSettings={visualSettings}
                visualType={visualType}
                widgetName={WidgetName}
                runtimeName={ChatRuntimeName}
            />
        </StyledOuterWrapper>
    );
};

const StyledOuterWrapper = styled(
    Box,
    {
        shouldForwardProp: (propName) =>
            propName !== "isFullSize" && propName !== "constrainToSystemWidth" && propName !== "height",
    }
)<{ isFullSize: boolean; minHeight?: string; constrainToSystemWidth: boolean; height?: string }>(
    ({ theme, isFullSize, minHeight, constrainToSystemWidth, height }) => ({
        "&": constrainToSystemWidth && systemWidth({ theme }),
        minHeight,
        height,
        ...(
            !constrainToSystemWidth && {
                position: "relative",
                width: "100%",
                height: "100%",
            }
        ),
        ...(
            isFullSize && {
                // full size mode - flex column to allow inner content to stretch
                display: "flex",
                flexDirection: "column",
                "& > div:first-of-type": {
                    display: "flex",
                    flex: "1",
                },
            }
        ),
    })
);
